import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import FormHelperText from '@mui/material/FormHelperText';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { CONTACT_METHOD_KEY, type AppConfigRoot, type CommunicationTemplate } from '@a1/shared';

import callAzureFunction from 'utils-ts/call-azure-function';
import handleError from 'utils-ts/handle-error';
import { resolveTemplateName } from 'hooks-ts/useGetEntity';
import { TipTapEditor } from 'ui-component/TipTapEditor';

interface EmailCommunicationMessageInputProps {
  communicationType: string;
  communicationSubtype?: string;
  communicationEntityName: string;
  emailMessageSubject: string;
  setEmailMessageSubject: Dispatch<SetStateAction<string>>;
  emailMessageBody: string;
  setEmailMessageBody: Dispatch<SetStateAction<string>>;
}

const EmailCommunicationMessageInput = ({
  communicationType,
  communicationSubtype,
  communicationEntityName,
  emailMessageSubject,
  setEmailMessageSubject,
  emailMessageBody,
  setEmailMessageBody
}: EmailCommunicationMessageInputProps) => {
  const appConfig = useSelector((state: AppConfigRoot) => state.appConfig?.data);

  const [emailCommunicationTemplates, setEmailCommunicationTemplates] = useState<CommunicationTemplate[]>([]);
  const [loadingEmailCommunicationTemplates, setLoadingEmailCommunicationTemplates] = useState(true);
  const [selectedEmailCommunicationTemplateId, setSelectedEmailCommunicationTemplateId] = useState('');

  useEffect(() => {
    const controller = new AbortController();

    const getEmailCommunicationTemplates = async () => {
      try {
        setLoadingEmailCommunicationTemplates(true);
        const response = await callAzureFunction<{ rows?: CommunicationTemplate[] }>({
          url: 'communication-templates',
          method: 'get',
          params: { page: 0, pageSize: 100, type: communicationType },
          signal: controller.signal
        });
        const communicationTemplates = response.data?.rows ?? [];
        setEmailCommunicationTemplates(
          communicationTemplates.filter(
            (communicationTemplate) =>
              String(communicationTemplate.contactMethod).toUpperCase() === CONTACT_METHOD_KEY.EMAIL &&
              (!communicationSubtype || communicationTemplate.subtype === communicationSubtype)
          )
        );
      } catch (error) {
        if (!controller.signal.aborted) {
          handleError(error);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingEmailCommunicationTemplates(false);
        }
      }
    };

    getEmailCommunicationTemplates();

    return () => {
      controller.abort();
    };
  }, [communicationSubtype, communicationType]);

  const handleEmailCommunicationTemplateChange = (event: SelectChangeEvent<string>) => {
    const communicationTemplateId = event.target.value;
    const selectedCommunicationTemplate = emailCommunicationTemplates.find(
      (communicationTemplate) => String(communicationTemplate.id) === communicationTemplateId
    );
    setSelectedEmailCommunicationTemplateId(communicationTemplateId);
    setEmailMessageSubject(selectedCommunicationTemplate?.messageSubject || '');
    setEmailMessageBody(selectedCommunicationTemplate?.messageBody || '');
  };

  const lowerCaseCommunicationEntityName = communicationEntityName.toLowerCase();

  return (
    <Stack gap={2}>
      <Select
        fullWidth
        displayEmpty
        size="small"
        input={<OutlinedInput />}
        value={selectedEmailCommunicationTemplateId}
        onChange={handleEmailCommunicationTemplateChange}
        disabled={loadingEmailCommunicationTemplates}
        renderValue={(communicationTemplateId) => {
          const selectedCommunicationTemplate = emailCommunicationTemplates.find(
            (communicationTemplate) => String(communicationTemplate.id) === String(communicationTemplateId)
          );
          if (!selectedCommunicationTemplate) {
            return <Typography color="text.disabled">{`Select ${lowerCaseCommunicationEntityName} communication`}</Typography>;
          }
          return resolveTemplateName(selectedCommunicationTemplate.name, appConfig);
        }}
      >
        {emailCommunicationTemplates.map((communicationTemplate) => (
          <MenuItem key={communicationTemplate.id} value={String(communicationTemplate.id)}>
            {resolveTemplateName(communicationTemplate.name, appConfig)}
          </MenuItem>
        ))}
      </Select>
      {!loadingEmailCommunicationTemplates && emailCommunicationTemplates.length === 0 && (
        <FormHelperText sx={{ ml: 1, mt: -1.5 }}>{`No email ${lowerCaseCommunicationEntityName} communications found.`}</FormHelperText>
      )}
      {selectedEmailCommunicationTemplateId && (
        <Stack gap={1}>
          <TextField
            label="Email Subject"
            value={emailMessageSubject}
            onChange={(event) => setEmailMessageSubject(event.target.value)}
            fullWidth
          />
          <TipTapEditor data={emailMessageBody} onChange={setEmailMessageBody} />
          <FormHelperText>
            Changes apply only to this notification and will not update the {lowerCaseCommunicationEntityName} communication.
          </FormHelperText>
        </Stack>
      )}
    </Stack>
  );
};

export default EmailCommunicationMessageInput;
